/**
 * features/hr/AppraisalsCenter.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — Appraisals (school-wide).
 *
 * Three sections in priority order:
 *   1. IN REVIEW           cycles awaiting a rating — actionable;
 *                          click 1–5 stars to complete inline
 *   2. RECENTLY COMPLETED  cycles completed within the last 6 months
 *   3. DRAFT               cycles created but not yet started
 *
 * Older completed cycles are accessible via the staff profile —
 * this screen surfaces what needs the HR officer's attention.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Star, Loader2, Hourglass, CheckCircle2, FileText } from 'lucide-react';
import { GradientHero, SoftCard, StatChip } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useHrData } from '@/features/hr/useHrData';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import {
  staffFullName,
  staffInitials,
  type AppraisalStatus,
  type HrAppraisal,
  type HrStaff,
} from '@/lib/hr';

function Stars({
  n,
  onPick,
  disabled,
}: {
  n?: number;
  onPick?: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={(e) => {
            if (onPick) {
              e.stopPropagation();
              onPick(i);
            }
          }}
          disabled={disabled || !onPick}
          className={`${onPick ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'} disabled:opacity-50`}
          aria-label={`Rate ${i} star${i === 1 ? '' : 's'}`}
        >
          <Star
            size={16}
            className={
              n && i <= n
                ? 'text-amber-400 fill-amber-400'
                : 'text-gray-300 dark:text-gray-600'
            }
          />
        </button>
      ))}
    </span>
  );
}

interface Props {
  onOpenStaff: (staffId: string) => void;
}

export default function AppraisalsCenter({ onOpenStaff }: Props) {
  const data = useHrData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const [busyId, setBusyId] = useState<string | null>(null);

  const enriched = useMemo(() => {
    return data.appraisals
      .map((a) => ({ appraisal: a, staff: data.staffById(a.staffId) }))
      .filter(
        (r): r is { appraisal: HrAppraisal; staff: HrStaff } => !!r.staff,
      );
  }, [data.appraisals, data.staffById]);

  const inReview = useMemo(
    () =>
      enriched
        .filter((r) => r.appraisal.status === 'in_review')
        .sort((a, b) =>
          staffFullName(a.staff).localeCompare(staffFullName(b.staff)),
        ),
    [enriched],
  );

  const recentlyCompleted = useMemo(() => {
    const cutoff = Date.now() - 180 * 86400000;
    return enriched
      .filter((r) => r.appraisal.status === 'completed')
      .filter((r) => {
        const t = r.appraisal.createdAt;
        if (!t) return true;
        return new Date(t).getTime() >= cutoff;
      })
      .sort(
        (a, b) =>
          (b.appraisal.createdAt ?? '').localeCompare(
            a.appraisal.createdAt ?? '',
          ),
      );
  }, [enriched]);

  const drafts = useMemo(
    () =>
      enriched
        .filter((r) => r.appraisal.status === 'draft')
        .sort((a, b) =>
          staffFullName(a.staff).localeCompare(staffFullName(b.staff)),
        ),
    [enriched],
  );

  const counts = useMemo(() => {
    const c = { completed: 0, in_review: 0, draft: 0 };
    for (const a of data.appraisals) c[a.status] += 1;
    return c;
  }, [data.appraisals]);

  async function complete(id: string, rating: number) {
    setBusyId(id);
    try {
      await hr.appraisals.update(id, { status: 'completed', rating });
      await recordHrAudit({
        action: 'appraisal.update',
        actor,
        subjectId: id,
        after: { status: 'completed', rating },
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Star}
        title="Appraisals"
        subtitle="Performance review cycles"
      />

      {data.loading ? (
        <div className="flex justify-center py-16">
          <Spinner label="Loading appraisals…" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatChip tone="gold" icon={Hourglass} value={counts.in_review} label="In review" />
            <StatChip tone="emerald" icon={CheckCircle2} value={counts.completed} label="Completed" />
            <StatChip tone="slate" icon={FileText} value={counts.draft} label="Draft" />
          </div>

          {/* In review */}
          {inReview.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <Hourglass size={14} className="text-amber-600 dark:text-amber-400" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-300">
                  Awaiting rating
                </span>
                <span className="text-[11px] text-gray-400">{inReview.length}</span>
              </div>
              <div className="space-y-2">
                {inReview.map(({ appraisal, staff }) => (
                  <AppraisalCard
                    key={appraisal.id}
                    appraisal={appraisal}
                    staff={staff}
                    onOpenStaff={() => onOpenStaff(staff.id)}
                    onComplete={(rating) => complete(appraisal.id, rating)}
                    busy={busyId === appraisal.id}
                    accent="amber"
                  />
                ))}
              </div>
            </section>
          )}

          {/* Recently completed */}
          {recentlyCompleted.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-300">
                  Recently completed
                </span>
                <span className="text-[11px] text-gray-400">{recentlyCompleted.length}</span>
              </div>
              <div className="space-y-2">
                {recentlyCompleted.map(({ appraisal, staff }) => (
                  <AppraisalCard
                    key={appraisal.id}
                    appraisal={appraisal}
                    staff={staff}
                    onOpenStaff={() => onOpenStaff(staff.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Draft */}
          {drafts.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <FileText size={14} className="text-gray-400" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                  Draft
                </span>
                <span className="text-[11px] text-gray-400">{drafts.length}</span>
              </div>
              <div className="space-y-2">
                {drafts.map(({ appraisal, staff }) => (
                  <AppraisalCard
                    key={appraisal.id}
                    appraisal={appraisal}
                    staff={staff}
                    onOpenStaff={() => onOpenStaff(staff.id)}
                    muted
                  />
                ))}
              </div>
            </section>
          )}

          {enriched.length === 0 && (
            <SoftCard hover={false}>
              <p className="text-sm text-gray-500 text-center py-6">
                No appraisals on file yet. Start a cycle from any staff profile.
              </p>
            </SoftCard>
          )}
        </>
      )}
    </div>
  );
}

function AppraisalCard({
  appraisal,
  staff,
  onOpenStaff,
  onComplete,
  busy,
  accent,
  muted,
}: {
  appraisal: HrAppraisal;
  staff: HrStaff;
  onOpenStaff: () => void;
  onComplete?: (rating: number) => void;
  busy?: boolean;
  accent?: 'amber';
  muted?: boolean;
}) {
  // Awaiting-rating cards get a subtle amber border to flag attention.
  // Everything else uses the standard card border. Status is conveyed
  // by the stars and the badge.
  const border =
    accent === 'amber'
      ? 'border border-amber-200 dark:border-amber-900/40'
      : 'border border-gray-100 dark:border-gray-700';
  return (
    <div
      onClick={onOpenStaff}
      className={`rounded-2xl bg-white dark:bg-gray-800 ${border} px-4 py-3 hover:shadow-md transition-shadow cursor-pointer ${muted ? 'opacity-95' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-[var(--accent-link-dark,#93c5fd)] flex items-center justify-center text-sm font-bold shrink-0">
          {staffInitials(staff)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
            {staffFullName(staff)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Cycle {appraisal.cycle} · {staff.jobTitle}
          </p>
        </div>
        {appraisal.status === 'in_review' && onComplete ? (
          <div className="flex items-center gap-2 shrink-0">
            {busy ? (
              <Loader2 size={14} className="animate-spin text-gray-400" />
            ) : (
              <Stars n={0} onPick={(v) => onComplete(v)} />
            )}
          </div>
        ) : (
          <Stars n={appraisal.rating} />
        )}
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-lg whitespace-nowrap shrink-0 ${BADGE_TONE[appraisal.status]}`}
        >
          {appraisal.status.replace('_', ' ')}
        </span>
      </div>
    </div>
  );
}

const BADGE_TONE: Record<AppraisalStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  in_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
};
